import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Users,
  MapPin,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import clsx from 'clsx'
import { getGrazingCurrent } from '../../services/api'

// Owner-view plan, Session 3.4 — one row per currently-grazing herd_group.
// Amber row highlight when the herd has been there >7 days AND vegetation
// is stressed/moderate — the "rotate now" case the anomaly strip's
// overdue_rotation detector will also flag.

const ROTATE_NOW_HEALTH = new Set(['stressed', 'moderate'])
const HEALTH_TEXT = {
  stressed: 'text-red-700',
  moderate: 'text-amber-700',
  healthy: 'text-emerald-700',
  vigorous: 'text-green-800',
}

function needsRotation(herd) {
  return (
    herd.days_in_paddock > 7
    && ROTATE_NOW_HEALTH.has(herd.vegetation_health)
  )
}

function daysLabel(days) {
  if (days === 0) return 'Today'
  if (days === 1) return '1 day'
  return `${days} days`
}

function Delta({ delta }) {
  if (delta == null) return null
  if (Math.abs(delta) < 0.2) return null
  const down = delta < 0
  const Icon = down ? TrendingDown : TrendingUp
  return (
    <span className={clsx(
      'inline-flex items-center gap-0.5 text-[11px]',
      down ? 'text-red-700' : 'text-emerald-700',
    )}>
      <Icon className="h-3 w-3" />
      {down ? '' : '+'}{delta.toFixed(1)}σ
    </span>
  )
}

export default function GrazingNowCard({ ranchId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['grazingCurrent', ranchId],
    queryFn: ({ signal }) => getGrazingCurrent(ranchId, { signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  const herds = data?.herds || []

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600" />
          Grazing Now
        </h3>
        <span className="text-xs text-gray-500">
          {herds.length} active herd{herds.length === 1 ? '' : 's'}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse bg-gray-50 rounded-lg" />
          ))}
        </div>
      ) : herds.length === 0 ? (
        <div className="h-40 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Users className="h-10 w-10 mb-2 opacity-30" />
          <p>No herds currently grazing.</p>
          <p className="text-xs mt-1 text-gray-400">
            Open grazing events appear here — enter one from the Paddocks page.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {herds.map((herd) => {
            const rotate = needsRotation(herd)
            return (
              <li
                key={`${herd.herd_group}-${herd.paddock_id}`}
                className={clsx(
                  'flex items-center justify-between gap-3 p-3 rounded-lg border',
                  rotate
                    ? 'bg-amber-50 border-amber-200'
                    : herd.unexplained_drop
                    ? 'bg-red-50/40 border-red-200'
                    : 'bg-white border-gray-200',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {herd.herd_group}
                    </p>
                    {herd.unexplained_drop && (
                      <AlertTriangle
                        className="h-3.5 w-3.5 text-red-600 flex-shrink-0"
                        aria-label="Unexplained vegetation drop"
                      />
                    )}
                  </div>
                  <Link
                    to="/paddocks"
                    className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-primary-700 mt-0.5"
                  >
                    <MapPin className="h-3 w-3" />
                    {herd.paddock_name || herd.paddock_code}
                  </Link>
                </div>
                <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                  <span className={clsx(
                    'text-xs font-medium',
                    rotate ? 'text-amber-800' : 'text-gray-700',
                  )}>
                    {daysLabel(herd.days_in_paddock)} in paddock
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={clsx(
                      'text-[11px] capitalize',
                      HEALTH_TEXT[herd.vegetation_health] || 'text-gray-500',
                    )}>
                      {herd.vegetation_health || 'unknown'}
                    </span>
                    <Delta delta={herd.delta_14d} />
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {herds.some(needsRotation) && (
        <p className="mt-3 text-[11px] text-amber-700">
          Amber rows: herd has been in a stressed/moderate paddock &gt;7 days — consider rotating.
        </p>
      )}
    </div>
  )
}
